import { useToken } from 'hooks/token'
import { useCallback, useState } from 'react'
import { Ticket } from 'types/Event'
import { Seat, SeatStatus } from 'types/Location'
import { generateSeatLabelData } from 'utils/seatLabel'
import { getSelectedTicketsForReleaseFromStorage } from 'utils/sessionStorage'

export const useAuditoriumReleaseSeats = (
    seats: Seat[],
    onSelectedSeatsChange: (selectedSeats: string[]) => void,
    tickets: Ticket[],
    eventId: string,
) => {
    const { sessionId } = useToken()

    // Track temporarily selected seats before backend confirms
    const [optimisticSeats, setOptimisticSeats] = useState<string[]>([])

    const getTicketStatusOptimistic = (row: number, column: number): SeatStatus => {
        const foundSeat = seats.find((s) => s.row === row && s.column === column)
        if (!foundSeat) return SeatStatus.NotAvailable

        // If seat is in the optimistic selection, treat it as selected
        if (foundSeat.seat_id && optimisticSeats.includes(foundSeat.seat_id)) {
            return SeatStatus.Selected
        }

        const ticketInfo = tickets.find((ticket) => ticket.seat_id === foundSeat.seat_id)
        if (!ticketInfo) return SeatStatus.NotAvailable

        if (ticketInfo.status === SeatStatus.Reserved) {
            return ticketInfo.locked_by === sessionId ? SeatStatus.Selected : SeatStatus.Reserved
        }

        return ticketInfo.status
    }

    const getTicketPrice = (seatId: string | undefined): number | 'N/A' => {
        if (!seatId) return 'N/A'
        const ticketInfo = tickets?.find((ticket) => ticket.seat_id === seatId)
        return ticketInfo ? ticketInfo.price : 'N/A'
    }

    const handleSeatClick = useCallback(
        (row: number, column: number) => {
            const selectedTickets = getSelectedTicketsForReleaseFromStorage(eventId)
            const foundSeat = seats.find((s) => s.row === row && s.column === column)
            const foundTicket = tickets.find((t) => t.seat_id === foundSeat?.seat_id)

            if (!foundSeat || !foundTicket) return

            const isSelected = selectedTickets.includes(foundSeat.seat_id)

            // Toggle seat
            const updatedSelection = isSelected
                ? selectedTickets.filter((id: string | undefined) => id !== foundSeat.seat_id)
                : [...selectedTickets, foundSeat.seat_id]

            setOptimisticSeats(updatedSelection)
            onSelectedSeatsChange(updatedSelection)
        },
        [seats, tickets, eventId, onSelectedSeatsChange],
    )

    return {
        getTicketStatusOptimistic,
        getTicketPrice,
        generateSeatLabelData,
        handleSeatClick,
        selectedTickets: getSelectedTicketsForReleaseFromStorage(eventId),
    }
}
